import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

/**
 * Legend tab content for dept / discipline / role codes.
 * Used wherever an employee list is shown or feeds a picker.
 */
export default function EmployeeCodeLegendPanel({ legend: legendProp, accent = '#1e3a5f' }) {
  const { instance, accounts } = useMsal();
  const [legend, setLegend] = useState(legendProp || null);

  useEffect(() => {
    if (legendProp) {
      setLegend(legendProp);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedApiCall(instance, accounts, 'GET', '/employees/code-legend');
        if (!cancelled && res.data?.success) setLegend(res.data.data);
      } catch (e) {
        console.error('Failed to load code legend', e);
      }
    })();
    return () => { cancelled = true; };
  }, [legendProp, instance, accounts]);

  if (!legend) {
    return <p style={{ color: '#6b7280', fontSize: 13 }}>Loading legend…</p>;
  }

  const blocks = [
    ['Departments', legend.departments, null],
    ['Disciplines (= Trade)', legend.disciplines, 'Matches the Accounting → Coding Structure disciplines (Maestro labour ranges shown).'],
    ['Roles', legend.roles, 'Maestro operation codes from the Accounting coding structure shown where they map.'],
    ...(legend.legacyDisciplines?.length
      ? [['Legacy discipline values', legend.legacyDisciplines, 'Still on some rows — being reclassified with Accounting.']]
      : []),
  ];

  return (
    <div>
      <p style={{ fontSize: 13, color: '#374151', marginTop: 0 }}>
        Codes are 2–3 letters (prefer 3). Every master employee should have department, discipline, and role.
        {' '}<strong>Discipline and Trade are the same thing</strong> — the master keeps one field.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        {blocks.map(([title, rows, hint]) => (
          <div
            key={title}
            style={{
              background: '#f8fafc',
              border: `1px solid ${accent}22`,
              borderRadius: 10,
              padding: '12px 14px',
            }}
          >
            <div style={{ fontWeight: 700, color: accent, marginBottom: 8, fontSize: 13 }}>{title}</div>
            {hint && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>{hint}</div>}
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.55, color: '#1f2937' }}>
              {(rows || []).map((r) => (
                <li key={r.code}>
                  <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: 3 }}>{r.code}</code>
                  {' '}{r.label}
                  {r.maestro && <span style={{ color: '#6b7280' }}> · Maestro {r.maestro}</span>}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Simple Help shell with Process / Legend tabs */
export function EmployeeHelpShell({
  open,
  onClose,
  title = 'Employee help',
  accent = '#1e3a5f',
  guideId,
  ListenButton,
  processContent,
  extraTabs = [],
  legend,
}) {
  const [tab, setTab] = useState('process');
  useEffect(() => {
    if (open) setTab('process');
  }, [open]);

  if (!open) return null;

  const tabs = [
    { id: 'process', label: 'Process' },
    { id: 'legend', label: 'Legend' },
    ...extraTabs.map((t) => ({ id: t.id, label: t.label })),
  ];

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, maxWidth: 920, width: '100%',
          maxHeight: '92vh', overflow: 'auto', padding: '1.25rem 1.5rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: accent }}>{title}</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {ListenButton && guideId ? (
              <ListenButton compact guideId={guideId} title={title} accent={accent} />
            ) : null}
            <button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer' }}>×</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid #e5e7eb', marginBottom: 14, flexWrap: 'wrap' }}>
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              style={{
                border: 'none',
                background: tab === t.id ? accent : 'transparent',
                color: tab === t.id ? '#fff' : accent,
                padding: '8px 14px',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'process' && processContent}
        {tab === 'legend' && <EmployeeCodeLegendPanel legend={legend} accent={accent} />}
        {extraTabs.map((t) => (tab === t.id ? <div key={t.id}>{t.content}</div> : null))}
      </div>
    </div>
  );
}
